// ABOUTME: A card that opens over what a mouse rests on — a badge's preview — and can be worked in: the pointer crosses
// ABOUTME: over to it, it stays while pinned, and Escape (its own, through the overlay stack) or a press outside close it.
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react"
import { createPortal } from "react-dom"
import { useZIndex } from "@shared/hooks/useZIndex"
import { OnTopProvider, useEscapeOwnership } from "./escapeStack"
import { HoverCardContext } from "./hoverCard"

interface CHoverCardProps {
  /** What the pointer rests on. */
  children: ReactNode
  /** Mounted only while the card is open; it closes or pins the card through `useHoverCard`. */
  content: ReactNode
  /** The card's name for screen readers. */
  label: string
  /** How long a mouse rests before it opens: a pass across the trigger opens nothing. */
  openDelay?: number
  /** How long it waits for the pointer to cross between the trigger and the card. */
  closeDelay?: number
  /** The card's size. Set it whole where the content loads in parts: a card grown with each part jumps over its
   *  trigger, and may flip below it once it no longer fits above. */
  className?: string
}

const GAP = 8

/** Through the portal these would reach what holds the trigger — a calendar card opens and drags on them. */
const stop = (event: SyntheticEvent) => event.stopPropagation()

/**
 * Escape goes on to `document`, where the card's own listener closes it: React stops the native event along with
 * its own, at the portal's container, and a stopped Escape typed in the card's field would never get there.
 */
const stopAllButEscape = (event: ReactKeyboardEvent) => {
  if (event.key !== "Escape") event.stopPropagation()
}

/**
 * Only a mouse opens it. A touch has no hover — there a tap is the trigger's own (the badge opens the window) —
 * and the keyboard reaches the same through the trigger.
 */
const isMouse = (event: PointerEvent) => event.pointerType === "mouse"

export const CHoverCard = ({
  children,
  content,
  label,
  openDelay = 300,
  closeDelay = 200,
  className = "",
}: CHoverCardProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const zIndex = useZIndex(isOpen)
  const isTopmostOverlay = useEscapeOwnership(isOpen, zIndex)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPinned = useRef(false)
  const isPointerOver = useRef(false)

  const clearTimer = useCallback(() => {
    if (timer.current === null) return
    clearTimeout(timer.current)
    timer.current = null
  }, [])

  const close = useCallback(() => {
    clearTimer()
    isPinned.current = false
    setIsOpen(false)
  }, [clearTimer])

  const closeLater = useCallback(() => {
    clearTimer()
    timer.current = setTimeout(close, closeDelay)
  }, [clearTimer, close, closeDelay])

  const pin = useCallback(
    (next: boolean) => {
      isPinned.current = next
      // Pinned while it waits to close — the pointer had just left as text came into its field: it stays.
      if (next) clearTimer()
      // Let go while the pointer is elsewhere: nothing holds it any more.
      else if (!isPointerOver.current) closeLater()
    },
    [clearTimer, closeLater],
  )
  const controls = useMemo(() => ({ close, pin }), [close, pin])

  // A timer left running would set the state of a card that is gone.
  useEffect(() => clearTimer, [clearTimer])

  const enterTrigger = (event: PointerEvent) => {
    if (!isMouse(event)) return
    isPointerOver.current = true
    clearTimer()
    if (!isOpen) timer.current = setTimeout(() => setIsOpen(true), openDelay)
  }
  // A press on the trigger stops an open still waiting; the click that follows closes an open card. Not the press: the
  // card may hold the trigger itself (a badge kept while its preview is open), and the click must still find it there.
  const pressTrigger = () => {
    if (!isOpen) clearTimer()
  }
  // And only once the trigger's own handler has run. A browser checks for microtasks between the root's capture and
  // bubble listeners, and React renders in one (react-dom-client.development.js:18979-18993): closed in the capture
  // phase, the card let go of a trigger it held before the trigger's click reached it. A task comes after both.
  const clickTrigger = () => {
    clearTimer()
    timer.current = setTimeout(close, 0)
  }
  const enterCard = (event: PointerEvent) => {
    if (!isMouse(event)) return
    isPointerOver.current = true
    clearTimer()
  }
  const leave = (event: PointerEvent) => {
    if (!isMouse(event)) return
    isPointerOver.current = false
    clearTimer()
    // Gone onto an overlay opened from the card — a picture on the whole screen — the pointer has not left it.
    if (isOpen && !isPinned.current && isTopmostOverlay()) closeLater()
  }

  // Escape is the card's while it is the overlay on top; a press anywhere but on the card or its trigger closes it.
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || !isTopmostOverlay()) return
      event.preventDefault()
      close()
    }
    const onPointerDown = (event: Event) => {
      // A press on an overlay opened from the card is that overlay's; one on the trigger waits for its click.
      if (!isTopmostOverlay()) return
      const target = event.target instanceof Node ? event.target : null
      if (target && (cardRef.current?.contains(target) || anchorRef.current?.contains(target))) return
      close()
    }
    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("pointerdown", onPointerDown, true)
    }
  }, [isOpen, isTopmostOverlay, close])

  // Above the trigger where it fits, below where it does not, inside the screen. Written to the node rather than
  // kept in state: it follows the page's scroll and its own growth as the content loads.
  const isShown = isOpen && zIndex !== 0
  useLayoutEffect(() => {
    const card = cardRef.current
    if (!isShown || !card) return
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect()
      if (!anchor) return
      const { width, height } = card.getBoundingClientRect()
      const above = anchor.top - GAP - height
      const fitsBelow = anchor.bottom + GAP + height <= window.innerHeight
      const top = above >= GAP || !fitsBelow ? Math.max(GAP, above) : anchor.bottom + GAP
      const left = Math.max(GAP, Math.min(anchor.left + anchor.width / 2 - width / 2, window.innerWidth - width - GAP))
      card.style.top = `${top}px`
      card.style.left = `${left}px`
      card.style.visibility = "visible"
    }
    place()
    const growth = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place)
    growth?.observe(card)
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      growth?.disconnect()
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [isShown])

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex shrink-0"
        onPointerEnter={enterTrigger}
        onPointerLeave={leave}
        // The trigger's own action has begun — the window it opens may still be loading, under a pointer that never
        // left: an open waiting its delay would put the card beneath that window. A keyboard's click as much as a press.
        onPointerDownCapture={pressTrigger}
        onClickCapture={clickTrigger}
      >
        {children}
      </span>
      {isShown &&
        createPortal(
          // Not a control: it keeps what happens on the card from reaching what holds the trigger.
          <div
            ref={cardRef}
            role="presentation"
            className={`fixed rounded-xl border border-gray-200 bg-white shadow-lg ${className}`}
            style={{ top: 0, left: 0, visibility: "hidden", zIndex }}
            onPointerEnter={enterCard}
            onPointerLeave={leave}
            onPointerDown={stop}
            onMouseDown={stop}
            onClick={stop}
            onDoubleClick={stop}
            onKeyDown={stopAllButEscape}
            onContextMenu={stop}
          >
            <div
              role="dialog"
              aria-label={label}
              className="h-full"
            >
              <HoverCardContext value={controls}>
                <OnTopProvider value={isTopmostOverlay}>{content}</OnTopProvider>
              </HoverCardContext>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
